import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BehaviorSubject, map, Observable } from "rxjs";
import { Department } from "../../../model/departments";
import { Messages } from "../../../model/messages";
import { AuthService } from "../../../auth.service";
import { Router } from "@angular/router";
import { DepartmentsService } from "../../../services/departaments.service";
import { ActionButtonSubmit } from "../../../model/action-button-submit";
import { NavigationHelper } from "../../../services/navigation.helper";
import { Navigation } from "../../../model/navigation";

@Component({
  selector: "departments-details",
  templateUrl: "./departments-details.component.html",
  styleUrls: ["./departments-details.component.css"],
})
export class DepartmentsDetailsComponent implements OnInit {
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  department = new BehaviorSubject<Department>(new Department(0, ""));
  editMode: boolean = false;
  departmentForm: FormGroup;
  departmentTitle: string = "departmentDetails.title.exist.department";
  isLoading = false;
  isCreating: boolean = true;
  supervisor: boolean = false;
  constructor(
    private departmentsService: DepartmentsService,
    private auth: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private nav: NavigationHelper
  ) {}

  ngOnInit(): void {
    this.initializeForm();

    this.departmentsService.selectedDepartmentObservable.subscribe(
      (selectedDepartment) => {
        if (selectedDepartment) {
          this.messages.next(Messages.EMPTY);
          this.isCreating = selectedDepartment.id === -9;

          if (
            selectedDepartment === this.department.value ||
            selectedDepartment.id === this.department.value.id
          ) {
            this.department.next(selectedDepartment);
          }
          this.populateFormWithDepartment(selectedDepartment);
        }
      }
    );

    this.nav.topic.subscribe((topic) => {
      this.adaptToNavigation();
    });
    this.adaptToNavigation();
    this.supervisor = this.auth.isSupervisor();
  }

  adaptToNavigation() {
    const current = this.nav.current;

    if (current.topic !== "departments") {
      return;
    }
    if (Navigation.NEW.toString() === current.id) {
      this.editMode = true;
      this.departmentForm.reset();
      this.departmentTitle = "departmentDetails.title.new.department";
    } else if (current.action === "edit") {
      this.editMode = true;
      this.departmentTitle = "departmentDetails.title.exist.department";
    } else {
      this.editMode = false;
      this.departmentTitle = "departmentDetails.title.exist.department";
    }

    const currentId = Number(current.id);
    if (currentId > 0) {
      const selectedDepartment = this.departmentsService.currentDepartment;
      const selectedDepartmentId = selectedDepartment?.id;
      if (selectedDepartmentId && selectedDepartmentId === currentId) {
        this.populateFormWithDepartment(selectedDepartment);
      } else {
        const token = this.auth.token;
        this.departmentsService
          .getDepartmentsById(token, currentId)
          .subscribe((messages) => {
            if (messages.hasAnyMessages) {
              this.messages.next(messages);
            } else {
            }
          });
      }
    }
  }

  toggleEditMode() {
    if (this.editMode) {
      this.editMode = false;
      this.messages.next(Messages.EMPTY);
      const selected = this.departmentsService.currentDepartment;
      if (selected && selected.id && selected.id > 0) {
        this.department.next(selected);
        this.departmentForm.patchValue({ name: selected.name || '' });
        this.departmentTitle = "departmentDetails.title.exist.department";
      } else {
        this.department.next(Department.EMPTY);
        this.departmentForm.reset();
      }
    } else {
      this.editMode = true;
    }
  }
  DepartmentEmpty(): boolean {
    const departmentValue = this.department.value;

    return (
      (!departmentValue ||
        departmentValue.id === null ||
        departmentValue.id === 0) &&
      !this.editMode
    );
  }
  private initializeForm(): void {
    this.departmentForm = this.fb.group({
      id: [{ value: 0, disabled: true }],
      name: ["", [Validators.required]],
      messages: this.messages,
    });
  }

  private populateFormWithDepartment(department: Department): void {
    this.department.next(department);
    this.departmentForm.patchValue({
      id: department.id,
      name: department.name,
    });
    this.editMode = department.id === Navigation.NEW;
    this.departmentTitle = this.editMode
      ? "departmentDetails.title.new.department"
      : "departmentDetails.title.exist.department";
  }

  saveDepartment(action: ActionButtonSubmit): void {
    this.isLoading = true;

    if (this.departmentForm.valid) {
      const token = this.auth.token;
      const id = this.department.value.id;

      const updatedDepartment = new Department(
        id === null || id < 1 ? null : id,
        this.departmentForm.value.name || ""
      );

      if (updatedDepartment.id === null || updatedDepartment.id < 1) {
        this.departmentsService
          .createDepartment(token, updatedDepartment)
          .subscribe(
            (messages) => {
              this.isLoading = false;
              this.messages.next(messages);

              if (messages.hasAnyMessages) {
                action.result(false);
              } else {
                this.updateDepartmentList(token);
                this.router.navigate(["departments"]);
                action.result(true);
              }
            },
            (error) => {
              this.isLoading = false;
              action.result(false);
              this.messages.next(
                Messages.from({
                  department: ["department.unknown.error", error],
                })
              );
            }
          );
      } else {
        this.departmentsService
          .updateDepartments(token, updatedDepartment)
          .subscribe(
            (messages) => {
              this.isLoading = false;
              this.messages.next(messages);

              if (messages.hasAnyMessages) {
                action.result(false);
              } else {
                this.updateDepartmentList(token);
                this.router.navigate(["departments"]);
                action.result(true);
              }
            },
            (error) => {
              this.isLoading = false;
              action.result(false);
              this.messages.next(
                Messages.from({
                  department: ["department.unknown.error", error],
                })
              );
            }
          );
      }
    } else {
      this.isLoading = false;
      action.result(false);
    }
  }

  deleteDepartment(): void {
    const token = this.auth.token;
    const departmentId = this.department.value.id;

    if (departmentId != null) {
      this.departmentsService.deleteDepartment(token, departmentId).subscribe(
        (messages) => {
          this.messages.next(messages);

          if (!messages.hasAnyMessages) {
            this.updateDepartmentList(token);
            this.department.next(Department.EMPTY);
          }
        },
        (error) => {
          this.messages.next(
            Messages.from({ departments: ["departments.delete.failed"] })
          );
        }
      );
    }
  }

  updateDepartmentList(token: string): void {
    this.departmentsService.departments(token).subscribe((messages) => {
      const updatedDepartments = this.departmentsService.departmentsData;
      this.departmentsService._currentDepartments.next(updatedDepartments);
    });
  }
  get currentDepartmentId(): Observable<number> {
    return this.department
      .asObservable()
      .pipe(map((department) => department.id || 0));
  }
  get TRUE(): boolean {
    return true;
  }
  get FALSE(): boolean {
    return false;
  }
  get isSupervisor(): boolean {
    return this.auth.userdata.supervisor;
  }
}
